import { useState, useCallback, useRef } from "react";

const defaultX = () => (typeof window !== "undefined" ? window.innerWidth / 2 : 400);
const defaultY = () => (typeof window !== "undefined" ? window.innerHeight / 2 : 300);

export function useOverlayTransform(initial = {}) {
  const [transform, setTransform] = useState({
    x: defaultX(),
    y: defaultY(),
    rotation: 0,
    scale: 1,
    ...initial,
  });
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const activeSessionRef = useRef(null);

  const endSession = useCallback((onMove, onUp) => {
    activeSessionRef.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
  }, []);

  const onPointerDownMove = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const t = transformRef.current;
      const session = {
        mode: "move",
        start: { x: t.x, y: t.y, rotation: t.rotation, scale: t.scale },
        startPt: { x: e.clientX, y: e.clientY },
      };
      activeSessionRef.current = session;

      const onMove = (ev) => {
        if (activeSessionRef.current !== session) return;
        const dx = ev.clientX - session.startPt.x;
        const dy = ev.clientY - session.startPt.y;
        setTransform({
          ...session.start,
          x: Math.min(window.innerWidth - 20, Math.max(20, session.start.x + dx)),
          y: Math.min(window.innerHeight - 20, Math.max(20, session.start.y + dy)),
        });
      };

      const onUp = () => endSession(onMove, onUp);

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [endSession]
  );

  const onPointerDownRotate = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const t = transformRef.current;
      const cx = t.x;
      const cy = t.y;
      const session = {
        mode: "rotate",
        startRot: t.rotation,
        startAngle: Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI),
        cx,
        cy,
      };
      activeSessionRef.current = session;

      const onMove = (ev) => {
        if (activeSessionRef.current !== session) return;
        const angle = Math.atan2(ev.clientY - session.cy, ev.clientX - session.cx) * (180 / Math.PI);
        const delta = angle - session.startAngle;
        let next = session.startRot + delta;
        if (ev.shiftKey) next = Math.round(next);
        setTransform((prev) => ({ ...prev, rotation: next }));
      };

      const onUp = () => endSession(onMove, onUp);

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [endSession]
  );

  const onPointerDownScale = useCallback(
    (e, opts = {}) => {
      e.preventDefault();
      e.stopPropagation();
      const { min = 0.5, max = 2.5, perPx = 0.006 } = opts;
      const t = transformRef.current;
      const session = {
        mode: "scale",
        startScale: t.scale ?? 1,
        startX: e.clientX,
      };
      activeSessionRef.current = session;

      const onMove = (ev) => {
        if (activeSessionRef.current !== session) return;
        const dx = ev.clientX - session.startX;
        const next = Math.min(max, Math.max(min, session.startScale + dx * perPx));
        setTransform((prev) => ({ ...prev, scale: next }));
      };

      const onUp = () => endSession(onMove, onUp);

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [endSession]
  );

  const onPointerDownLength = useCallback(
    (e, opts = {}) => {
      e.preventDefault();
      e.stopPropagation();
      const { min = 5, max = 30, perPx = 0.04, current = 15, onChange } = opts;
      const session = {
        mode: "length",
        startVal: current,
        startX: e.clientX,
      };
      activeSessionRef.current = session;

      const onMove = (ev) => {
        if (activeSessionRef.current !== session) return;
        const dx = ev.clientX - session.startX;
        const next = Math.min(max, Math.max(min, Math.round(session.startVal + dx * perPx)));
        if (typeof onChange === "function") onChange(next);
      };

      const onUp = () => endSession(onMove, onUp);

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [endSession]
  );

  return {
    transform,
    setTransform,
    onPointerDownMove,
    onPointerDownRotate,
    onPointerDownScale,
    onPointerDownLength,
  };
}
